import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getLandingDocumentUrl } from '../utils/landingDocumentUrl';

const LandingPageView = ({ landingPage: propLandingPage, isPreview = false }) => {
  const { url, tab } = useParams();
  const navigate = useNavigate();
  const [landingPage, setLandingPage] = useState(propLandingPage || null);
  const [loading, setLoading] = useState(!propLandingPage);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState((tab && tab.toLowerCase() === 'support') ? 'support' : 'overview');
  const [supportFormData, setSupportFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    description: '',
    category: 'General Question',
    priority: 'medium',
    language: 'en',
    productId: ''
  });
  const [submittingSupport, setSubmittingSupport] = useState(false);
  const [supportTicketSubmitted, setSupportTicketSubmitted] = useState(false);

  useEffect(() => {
    if (tab) {
      setActiveTab(tab.toLowerCase() === 'support' ? 'support' : 'overview');
    } else {
      setActiveTab('overview');
    }
  }, [tab]);

  useEffect(() => {
    // Preview modunda API çağrısı yapma
    if (isPreview && propLandingPage) {
      setLandingPage(propLandingPage);
      setLoading(false);
      
      // İlk product'ı seçili yap
      if (propLandingPage.products && propLandingPage.products.length > 0) {
        setSupportFormData(prev => ({
          ...prev,
          productId: propLandingPage.products[0]._id
        }));
      }
      return;
    }

    const fetchLandingPage = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/landing-pages/public/${url}`);
      
        if (!response.ok) {
          if (response.status === 404) {
              // Redirect to homepage if landing page not found
              window.location.href = '/';
              return;
            }
            throw new Error('Failed to fetch landing page');
          }
          
          const data = await response.json();
          console.log('Landing page data:', data);
          console.log('Documents:', data.documents);
          console.log('Selected documents:', data.selectedDocuments);
          setLandingPage(data);
          
          // İlk product'ı seçili yap
          if (data.products && data.products.length > 0) {
            setSupportFormData(prev => ({
              ...prev,
              productId: data.products[0]._id
            }));
          }
        } catch (err) {
          console.error('Error fetching landing page:', err);
          setError(err.message);
          // Redirect to homepage on error
          window.location.href = '/';
        } finally {
          setLoading(false);
        }
      };

      if (url) {
        fetchLandingPage();
      }
    }, [url, isPreview, propLandingPage]);

  const handleSupportFormChange = (e) => {
    const { name, value } = e.target;
    setSupportFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    
    // Preview modunda support form'unu devre dışı bırak
    if (isPreview) {
      toast.info('This is a preview. Support form is disabled in preview mode.');
      return;
    }
    
    if (!supportFormData.name || !supportFormData.email || !supportFormData.subject || !supportFormData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmittingSupport(true);
    try {
      // Get selected product for the support ticket
      const product = landingPage.products && landingPage.products.length > 0 
        ? landingPage.products.find(p => p._id === supportFormData.productId) || landingPage.products[0]
        : null;
      
      if (!product) {
        toast.error('No product found for support ticket');
        return;
      }

      const response = await fetch('/api/support/support-tickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          supportModuleId: landingPage.supportModule._id,
          productId: product._id,
          subject: supportFormData.subject,
          description: supportFormData.description,
          category: supportFormData.category,
          priority: supportFormData.priority,
          language: supportFormData.language,
          customerInfo: {
            name: supportFormData.name,
            email: supportFormData.email,
            phone: supportFormData.phone
          },
          customData: {
            source: 'landing_page',
            landingPageId: landingPage._id
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Support ticket created successfully!');
        setSupportTicketSubmitted(true);
        // Form'u temizle ama göster
        setSupportFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          description: '',
          category: 'General Question',
          priority: 'medium',
          language: 'en',
          productId: landingPage.products && landingPage.products.length > 0 ? landingPage.products[0]._id : ''
        });
      } else {
        toast.error(data.message || 'Failed to create support ticket');
      }
    } catch (error) {
      console.error('Error creating support ticket:', error);
      toast.error('Failed to create support ticket');
    } finally {
      setSubmittingSupport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading landing page...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Landing Page Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (!landingPage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Landing Page Not Found</h1>
          <p className="text-gray-600 mb-6">The requested landing page could not be found.</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  const headlineText = landingPage.content?.hero?.title || '';
  let bodyText = landingPage.content?.hero?.description || landingPage.description || '';
  let headline = headlineText;

  if (!headline && bodyText) {
    const dotIndex = bodyText.indexOf('.');
    if (dotIndex !== -1) {
      headline = bodyText.substring(0, dotIndex).trim();
      bodyText = bodyText.substring(dotIndex + 1).trim();
    } else {
      headline = bodyText;
      bodyText = '';
    }
  }

  const displayDomain = (landingPage.project?.website || landingPage.customUrl || '').replace(/https?:\/\//, '').replace(/\/$/, '').toUpperCase();

  const androidProduct = landingPage.products?.find(p => p.platform === 'android' || p.name?.toLowerCase().includes('android'));
  const iosProduct = landingPage.products?.find(p => p.platform === 'ios' || p.name?.toLowerCase().includes('ios'));

  const playStoreLink = androidProduct?.appStoreLinks?.googlePlay || (androidProduct ? `https://play.google.com/store/apps/details?id=${landingPage.project?.packageName || 'com.example.app'}` : null);
  const appStoreLink = iosProduct?.appStoreLinks?.appStore || (iosProduct ? `https://apps.apple.com/app/id${landingPage.project?.appStoreId || '123456789'}` : null);

  const hexToRgba = (hex, alpha) => {
    if (!hex) return `rgba(212, 175, 55, ${alpha})`;
    let c = hex.substring(1);
    if (c.length === 3) {
      c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
    }
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const primaryColor = landingPage.theme?.primaryColor || '#d4af37';
  const secondaryColor = landingPage.theme?.secondaryColor || '#9ca3af';
  const backgroundColor = landingPage.theme?.backgroundColor || '#000000';
  const textColor = landingPage.theme?.textColor || '#ffffff';

  return (
    <div 
      className={isPreview ? "w-full h-full overflow-auto relative" : "min-h-screen relative"}
      style={{ 
        backgroundColor: backgroundColor,
        color: textColor,
        fontFamily: "'Inter', sans-serif"
      }}
    >
      <style>{`
        .app-logo-hoverable:hover {
          transform: scale(1.02);
          box-shadow: 0 0 35px ${hexToRgba(primaryColor, 0.3)}, 0 20px 40px rgba(0, 0, 0, 0.8) !important;
        }
        .download-btn-hoverable:hover {
          background-color: ${hexToRgba(primaryColor, 0.1)} !important;
          border-color: ${primaryColor} !important;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px ${hexToRgba(primaryColor, 0.1)};
        }
        .footer-link-hoverable:hover {
          color: ${primaryColor} !important;
          opacity: 1 !important;
        }
        .support-input-field:focus {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 3px ${hexToRgba(primaryColor, 0.25)} !important;
        }
      `}</style>

      {/* Subtle radial gold gradient glow at top */}
      <div 
        className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-[600px] pointer-events-none"
        style={{
          background: `radial-gradient(circle at top, ${hexToRgba(primaryColor, 0.12)} 0%, transparent 70%)`
        }}
      />

      {/* Minimalist Top Navigation Header (only if support is active) */}
      {landingPage.supportModule && landingPage.supportModule.isActive && landingPage.supportModule._id && (
        <header className="w-full max-w-3xl mx-auto px-6 py-6 flex justify-end relative z-50">
          <div className="flex space-x-6">
            <button
              onClick={() => {
                if (isPreview) {
                  setActiveTab('overview');
                } else {
                  navigate(`/landing/${url}`);
                }
              }}
              className="text-xs font-semibold tracking-widest uppercase transition-colors duration-200"
              style={{
                color: activeTab === 'overview' ? primaryColor : secondaryColor
              }}
            >
              App
            </button>
            <button
              onClick={() => {
                if (isPreview) {
                  setActiveTab('support');
                } else {
                  navigate(`/landing/${url}/support`);
                }
              }}
              className="text-xs font-semibold tracking-widest uppercase transition-colors duration-200"
              style={{
                color: activeTab === 'support' ? primaryColor : secondaryColor
              }}
            >
              Support
            </button>
          </div>
        </header>
      )}

      {/* Main Content Area */}
      <main className="max-w-3xl mx-auto px-6 py-12 flex flex-col items-center text-center relative z-10">
        {activeTab === 'overview' ? (
          <div className="flex flex-col items-center w-full">
            {/* App Icon */}
            <img 
              src={landingPage.project?.logo || '/default-project.png'} 
              alt={landingPage.project?.name}
              className="app-logo-hoverable rounded-[32px] object-cover border-2 transition-all duration-300"
              onError={(e) => { e.target.src = '/default-project.png'; }}
              style={{ 
                width: '140px', 
                height: '140px',
                marginBottom: '48px',
                borderColor: hexToRgba(primaryColor, 0.3),
                boxShadow: `0 0 25px ${hexToRgba(primaryColor, 0.15)}, 0 20px 40px rgba(0,0,0,0.8)`
              }}
            />

            {/* App Name */}
            <h1 
              className="text-4xl sm:text-5xl font-bold mb-2 tracking-tight"
              style={{ color: textColor }}
            >
              {landingPage.title}
            </h1>

            {/* Headline */}
            {headline && (
              <h2 
                className="text-xl sm:text-3xl font-semibold leading-snug max-w-2xl mb-5 tracking-tight"
                style={{ color: textColor, opacity: 0.95 }}
              >
                {headline}
              </h2>
            )}

            {/* Paragraph Description */}
            {bodyText && (
              <p 
                className="text-base sm:text-lg leading-relaxed max-w-xl mb-10"
                style={{ color: secondaryColor }}
              >
                {bodyText}
              </p>
            )}

            {/* Download Buttons */}
            {(appStoreLink || playStoreLink) && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center w-full mb-10">
                {appStoreLink && (
                  <a 
                    href={appStoreLink} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="download-btn-hoverable flex items-center justify-start border rounded-2xl px-6 py-3 transition-all duration-250 w-[200px] mx-auto sm:mx-0"
                    style={{
                      backgroundColor: hexToRgba(backgroundColor, 0.8),
                      borderColor: hexToRgba(primaryColor, 0.15),
                      color: textColor
                    }}
                  >
                    <svg className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: textColor }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.06,16.66C19.97,16.67 18.71,19.5 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                    </svg>
                    <div className="text-left flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: secondaryColor, opacity: 0.8 }}>Download on the</span>
                      <span className="text-base font-semibold leading-tight">App Store</span>
                    </div>
                  </a>
                )}
                {playStoreLink && (
                  <a 
                    href={playStoreLink} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="download-btn-hoverable flex items-center justify-start border rounded-2xl px-6 py-3 transition-all duration-250 w-[200px] mx-auto sm:mx-0"
                    style={{
                      backgroundColor: hexToRgba(backgroundColor, 0.8),
                      borderColor: hexToRgba(primaryColor, 0.15),
                      color: textColor
                    }}
                  >
                    <svg className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: textColor }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="text-left flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: secondaryColor, opacity: 0.8 }}>Get it on</span>
                      <span className="text-base font-semibold leading-tight">Google Play</span>
                    </div>
                  </a>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Support Ticket View */
          <div 
            className="w-full text-left border rounded-3xl p-8 sm:p-12 shadow-2xl relative z-10 max-w-2xl mx-auto"
            style={{
              backgroundColor: hexToRgba(backgroundColor, 0.8),
              borderColor: hexToRgba(primaryColor, 0.15),
              color: textColor
            }}
          >
              {supportTicketSubmitted ? (
                <div className="text-center">
                  <div className="mb-8">
                    <div 
                      className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6"
                      style={{ backgroundColor: hexToRgba(primaryColor, 0.1) }}
                    >
                      <svg 
                        className="w-10 h-10"
                        style={{ color: primaryColor }}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold mb-4" style={{ color: textColor }}>
                      Support Request Received!
                    </h3>
                    <p className="mb-6 max-w-md mx-auto" style={{ color: secondaryColor }}>
                      Thank you for contacting us. Your support ticket has been successfully submitted and our team will get back to you as soon as possible.
                    </p>
                    <p className="text-sm" style={{ color: secondaryColor, opacity: 0.8 }}>
                      We typically respond within 24 hours during business days.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="w-full">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold mb-2" style={{ color: textColor }}>
                      Get Support
                    </h2>
                    <p className="max-w-md mx-auto" style={{ color: secondaryColor }}>
                      Need help? Our support team is here to assist you with any questions or issues.
                    </p>
                  </div>
                  <form onSubmit={handleSupportSubmit} className="space-y-4">
                    {/* Product Selection */}
                    {landingPage.products && landingPage.products.length > 1 && (
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: secondaryColor }}>
                          Product *
                        </label>
                        <select
                          name="productId"
                          value={supportFormData.productId}
                          onChange={handleSupportFormChange}
                          className="support-input-field w-full px-3 py-2 border rounded-xl focus:outline-none transition-all duration-200"
                          style={{
                            borderColor: hexToRgba(primaryColor, 0.2),
                            backgroundColor: hexToRgba(backgroundColor, 0.5),
                            color: textColor
                          }}
                          required
                        >
                          {landingPage.products.map((product) => (
                            <option key={product._id} value={product._id} style={{ backgroundColor: backgroundColor, color: textColor }}>
                              {product.name} ({product.platform || 'Unknown Platform'})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: secondaryColor }}>
                          Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={supportFormData.name}
                          onChange={handleSupportFormChange}
                          className="support-input-field w-full px-3 py-2 border rounded-xl focus:outline-none transition-all duration-200"
                          style={{
                            borderColor: hexToRgba(primaryColor, 0.2),
                            backgroundColor: hexToRgba(backgroundColor, 0.5),
                            color: textColor
                          }}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: secondaryColor }}>
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={supportFormData.email}
                          onChange={handleSupportFormChange}
                          className="support-input-field w-full px-3 py-2 border rounded-xl focus:outline-none transition-all duration-200"
                          style={{
                            borderColor: hexToRgba(primaryColor, 0.2),
                            backgroundColor: hexToRgba(backgroundColor, 0.5),
                            color: textColor
                          }}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: secondaryColor }}>
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={supportFormData.phone}
                        onChange={(e) => {
                          const value = e.target.value;
                          const filteredValue = value.replace(/[^+\d]/g, '');
                          setSupportFormData(prev => ({
                            ...prev,
                            phone: filteredValue
                          }));
                        }}
                        className="support-input-field w-full px-3 py-2 border rounded-xl focus:outline-none transition-all duration-200"
                        style={{
                          borderColor: hexToRgba(primaryColor, 0.2),
                          backgroundColor: hexToRgba(backgroundColor, 0.5),
                          color: textColor
                        }}
                        placeholder="+1234567890"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: secondaryColor }}>
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={supportFormData.subject}
                        onChange={handleSupportFormChange}
                        className="support-input-field w-full px-3 py-2 border rounded-xl focus:outline-none transition-all duration-200"
                        style={{
                          borderColor: hexToRgba(primaryColor, 0.2),
                          backgroundColor: hexToRgba(backgroundColor, 0.5),
                          color: textColor
                        }}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: secondaryColor }}>
                          Category
                        </label>
                        <select
                          name="category"
                          value={supportFormData.category}
                          onChange={handleSupportFormChange}
                          className="support-input-field w-full px-3 py-2 border rounded-xl focus:outline-none transition-all duration-200"
                          style={{
                            borderColor: hexToRgba(primaryColor, 0.2),
                            backgroundColor: hexToRgba(backgroundColor, 0.5),
                            color: textColor
                          }}
                        >
                          <option value="General Question" style={{ backgroundColor: backgroundColor, color: textColor }}>General Question</option>
                          <option value="Technical Issue" style={{ backgroundColor: backgroundColor, color: textColor }}>Technical Issue</option>
                          <option value="Feature Request" style={{ backgroundColor: backgroundColor, color: textColor }}>Feature Request</option>
                          <option value="Bug Report" style={{ backgroundColor: backgroundColor, color: textColor }}>Bug Report</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: secondaryColor }}>
                          Priority
                        </label>
                        <select
                          name="priority"
                          value={supportFormData.priority}
                          onChange={handleSupportFormChange}
                          className="support-input-field w-full px-3 py-2 border rounded-xl focus:outline-none transition-all duration-200"
                          style={{
                            borderColor: hexToRgba(primaryColor, 0.2),
                            backgroundColor: hexToRgba(backgroundColor, 0.5),
                            color: textColor
                          }}
                        >
                          <option value="low" style={{ backgroundColor: backgroundColor, color: textColor }}>Low</option>
                          <option value="medium" style={{ backgroundColor: backgroundColor, color: textColor }}>Medium</option>
                          <option value="high" style={{ backgroundColor: backgroundColor, color: textColor }}>High</option>
                          <option value="urgent" style={{ backgroundColor: backgroundColor, color: textColor }}>Urgent</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: secondaryColor }}>
                          Language
                        </label>
                        <select
                          name="language"
                          value={supportFormData.language}
                          onChange={handleSupportFormChange}
                          className="support-input-field w-full px-3 py-2 border rounded-xl focus:outline-none transition-all duration-200"
                          style={{
                            borderColor: hexToRgba(primaryColor, 0.2),
                            backgroundColor: hexToRgba(backgroundColor, 0.5),
                            color: textColor
                          }}
                        >
                          <option value="en" style={{ backgroundColor: backgroundColor, color: textColor }}>English</option>
                          <option value="tr" style={{ backgroundColor: backgroundColor, color: textColor }}>Türkçe</option>
                          <option value="es" style={{ backgroundColor: backgroundColor, color: textColor }}>Español</option>
                          <option value="fr" style={{ backgroundColor: backgroundColor, color: textColor }}>Français</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: secondaryColor }}>
                        Description *
                      </label>
                      <textarea
                        name="description"
                        value={supportFormData.description}
                        onChange={handleSupportFormChange}
                        rows={4}
                        className="support-input-field w-full px-3 py-2 border rounded-xl focus:outline-none transition-all duration-200"
                        style={{
                          borderColor: hexToRgba(primaryColor, 0.2),
                          backgroundColor: hexToRgba(backgroundColor, 0.5),
                          color: textColor
                        }}
                        placeholder="Please describe your issue in detail..."
                        required
                      />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setActiveTab('overview')}
                        className="px-4 py-2 border rounded-xl transition-all duration-200"
                        style={{
                          borderColor: hexToRgba(primaryColor, 0.2),
                          backgroundColor: 'transparent',
                          color: secondaryColor
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = hexToRgba(primaryColor, 0.05);
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submittingSupport}
                        className="px-6 py-2 rounded-xl transition-all duration-200 font-semibold disabled:opacity-50"
                        style={{
                          backgroundColor: primaryColor,
                          color: backgroundColor === '#ffffff' || backgroundColor === '#fff' ? '#ffffff' : '#000000'
                        }}
                        onMouseEnter={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.backgroundColor = hexToRgba(primaryColor, 0.95);
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.backgroundColor = primaryColor;
                          }
                        }}
                      >
                        {submittingSupport ? 'Submitting...' : 'Submit Ticket'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
          </div>
        )}
      </main>

      {/* Subtle gold divider line */}
      <div 
        className="w-full max-w-[320px] h-[1px] mx-auto my-10"
        style={{
          background: `linear-gradient(90deg, transparent, ${hexToRgba(primaryColor, 0.15)} 50%, transparent)`
        }}
      />

      {/* Center Footer */}
      <footer className="w-full max-w-3xl mx-auto px-6 pb-12 flex flex-col items-center">
        <div className="flex flex-wrap justify-center gap-6 mb-6">
          {landingPage.selectedDocuments && landingPage.selectedDocuments.length > 0 ? (
            landingPage.selectedDocuments.map((doc) => {
              if (!doc) return null;
              const docUrl = getLandingDocumentUrl(landingPage.customUrl || url, doc.type) || doc.fileUrl;
              if (!docUrl) return null;

              return (
                <a
                  key={doc._id}
                  href={docUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link-hoverable text-sm font-medium transition-colors duration-200"
                  style={{ color: secondaryColor }}
                >
                  {doc.name}
                </a>
              );
            })
          ) : null}
          {landingPage.supportModule && landingPage.supportModule.isActive && (
            <button
              onClick={() => {
                if (isPreview) {
                  setActiveTab('support');
                } else {
                  navigate(`/landing/${url}/support`);
                }
              }}
              className="footer-link-hoverable text-sm font-medium transition-colors duration-200"
              style={{ color: secondaryColor }}
            >
              Support
            </button>
          )}
          {landingPage.project?.website && (
            <a
              href={landingPage.project.website.startsWith('http') ? landingPage.project.website : `https://${landingPage.project.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link-hoverable text-sm font-medium transition-colors duration-200"
              style={{ color: secondaryColor }}
            >
              Website
            </a>
          )}
        </div>
        
        <div className="text-xs" style={{ color: secondaryColor, opacity: 0.5 }}>
          © {new Date(landingPage.project?.createdAt || landingPage.createdAt || new Date()).getFullYear()}{' '}
          {landingPage.project?.companyName || landingPage.project?.name || landingPage.title}. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default LandingPageView;
